/**
 * Number of significant decimal places needed to print `step` in fixed
 * notation, e.g. `precisionFixed(0.03) === 2`. Inlined from d3-format
 * (MIT licensed, https://github.com/d3/d3-format/blob/main/src/precisionFixed.js)
 * so this one helper doesn't pull in the whole package as a dependency.
 */
const precisionFixed = (step: number): number => {
  const exponential = Math.abs(step).toExponential();
  const exponentIndex = exponential.indexOf('e');
  const exponent =
    exponentIndex < 0 ? NaN : Number(exponential.slice(exponentIndex + 1));

  return Math.max(0, -exponent);
};

const PRICE_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatPrice = (value: number): string => {
  if (!Number.isFinite(value)) {
    return '—';
  }

  return PRICE_FORMATTER.format(value);
};

/**
 * Derive the decimal precision for a y-axis tick band from its step, the
 * way v1 `getYAxisTicks` does. Callers should compute this once per tick
 * band and reuse it across `formatYAxisTick` calls instead of re-deriving
 * it for every tick.
 */
export const yAxisTickDecimals = (ticks: number[]) => {
  const step = ticks.length >= 2 ? ticks[1] - ticks[0] : 0;
  return precisionFixed(step) || 0;
};

export const formatYAxisTick = (value: number, decimals: number) =>
  value.toLocaleString(window.navigator.language, {
    minimumFractionDigits: Math.max(2, decimals),
  });

export const formatDateTick = (ms: number, spanMs: number): string => {
  if (!Number.isFinite(ms) || !Number.isFinite(spanMs)) {
    return '—';
  }

  const d = new Date(ms);

  if (spanMs <= 1000 * 60 * 60 * 24 * 2) {
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'UTC',
    });
  }

  if (spanMs <= 1000 * 60 * 60 * 24 * 365 * 1.2) {
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      timeZone: 'UTC',
    });
  }

  return d.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

// Cached because `Intl.DateTimeFormat` construction is non-trivial
// and `formatTooltipTime` runs on every crosshair pointer-move frame.
const tooltipParts = (timeZone?: 'UTC') => ({
  weekday: new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone }),
  month: new Intl.DateTimeFormat('en-US', { month: 'short', timeZone }),
  time: new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone,
  }),
});
const TOOLTIP_UTC = tooltipParts('UTC');
const TOOLTIP_LOCAL = tooltipParts();

/**
 * Mirrors v1's `formatXAxisPoint` so the chartv2 crosshair label reads
 * the same as the legacy chart, e.g. `Thu 26 Mar'26` (or
 * `Thu 26 Mar'26 14:30` for intraday). Pass `useLocal` when the x-axis
 * is on hour-kind ticks so the pill matches those local labels.
 */
export const formatTooltipTime = (ms: number, useLocal = false): string => {
  if (!Number.isFinite(ms)) {
    return '—';
  }

  const d = new Date(ms);
  const parts = useLocal ? TOOLTIP_LOCAL : TOOLTIP_UTC;
  const day = useLocal ? d.getDate() : d.getUTCDate();
  const year = useLocal ? d.getFullYear() : d.getUTCFullYear();
  const hours = useLocal ? d.getHours() : d.getUTCHours();
  const minutes = useLocal ? d.getMinutes() : d.getUTCMinutes();
  const yy = (year % 100).toString().padStart(2, '0');
  const base = `${parts.weekday.format(d)} ${day} ${parts.month.format(d)}'${yy}`;

  // Drop the `HH:mm` suffix on day-aligned ticks — v1 only appends it
  // when the timestamp doesn't fall on a 00:00 boundary.
  if (hours === 0 && minutes === 0) {
    return base;
  }

  return `${base} ${parts.time.format(d)}`;
};
